import React, { useContext, useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import type { JSX } from 'react';
import type { Theme} from '@mui/material';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useTheme } from '@mui/material';
import type Order from '../../Models/OrderModel';
import type OrderValidationResult from '../../Models/OrderValidationResultModel';
import type { ApiContextProps } from '../../Contexts/ApiContext';
import ApiContext from '../../Contexts/ApiContext';
import type { TokenContextProps } from '../../Contexts/TokenContext';
import TokenContext from '../../Contexts/TokenContext';
import type OrderValidationResultMessage from '../../Models/OrderValidationResultMessageModel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { ErrorMessage, LoadingButton } from 'shouldve-been-mui';

type ApproveOrderDialogProps = {
    order: Order,
    controller: boolean,
    onClose: (isApproved: boolean) => void,
};

function ApproveOrderDialog(props: Readonly<ApproveOrderDialogProps>): JSX.Element {

    const apiContext: ApiContextProps = useContext<ApiContextProps>(ApiContext);
    const tokenContext: TokenContextProps = useContext<TokenContextProps>(TokenContext);

    const currentTheme: Theme = useTheme();

    const [orderValidationResult, setOrderValidationResult] = useState<OrderValidationResult | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [pending, setPending] = useState<boolean>(false);

    const [windowSize, setWindowSize] = useState<[number, number]>([1920, 1080]);

    const tableRows: JSX.Element[] = useMemo<JSX.Element[]>(() => orderValidationResult?.messages.map((message: OrderValidationResultMessage) => {

        const icon: IconProp | null = message.getIcon();
        const iconColor: string = message.getIconColor();

        return (
            <TableRow
                key={message.infoMessage}
            >
                <TableCell style={{
                    textAlign: 'center',
                }}>
                    {icon !== null &&
                        <FontAwesomeIcon
                            icon={icon}
                            color={iconColor}
                        />
                    }
                </TableCell>
                <TableCell style={{
                    maxWidth: Math.min(windowSize[0] * 0.8, 300),
                }}>
                    {message.infoMessage}
                </TableCell>
            </TableRow>
        );
    }) ?? [], [orderValidationResult?.messages, windowSize]);

    useEffect(() => {

        function updateSize(): void {
            setWindowSize([window.innerWidth, window.innerHeight]);
        }

        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    useEffect(() => {

        let mounted: boolean = true;

        async function validateOrder(): Promise<void> {

            const newOrderValidationResult: OrderValidationResult | null = await apiContext.api?.validateOrder(props.order.orderId, tokenContext.token) ?? null;

            if (mounted) {
                setOrderValidationResult(newOrderValidationResult);
            }
        }

        void validateOrder();

        return () => {
            mounted = false;
        };
    }, [apiContext.api, props.order.orderId, tokenContext.token]);

    async function handleClose(willApprove: boolean): Promise<void> {

        if (willApprove) {

            setPending(true);
            
            const success: boolean = await apiContext.api?.setStatusDate(props.order.orderId, 'approve', null, tokenContext.token) ?? false;

            setPending(false);

            if (success) {
                props.onClose(true);
            } else {
                setErrorMessage('Failed to approve order');
            }
        } else {
            props.onClose(false);
        }
    }

    return (
        <Dialog 
            open={props.controller} 
            onClose={(): void => props.onClose(false)}
            TransitionProps={{
                onEntered: (): void => setErrorMessage(null),
            }}
        >
            <DialogTitle>Approve Order - {(props.order.vesselCode ?? '') + '_' + (props.order.orderRef ?? '')}</DialogTitle>
            <DialogContent>
                {orderValidationResult === null
                    ?   (
                        <span style={{
                            color: currentTheme.palette.text.primary,
                        }}>
                            Could not show order
                        </span>
                    )
                    :   (
                        <>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                Level
                                            </TableCell>
                                            <TableCell>
                                                Message
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {tableRows}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <br />
                            <ErrorMessage
                                message={errorMessage}
                                maxWidth={Math.min(windowSize[0] * 0.8, 300)}
                            />
                        </>
                    )
                }
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={async (): Promise<void> => handleClose(false)}
                    color='error'
                    variant='contained'
                    style={{
                        width: '90px',
                        height: '32px',
                        lineHeight: 'normal',
                        marginRight: '10px',
                    }}
                >
                    Close
                </Button>
                <LoadingButton 
                    width='90px'
                    height='32px'
                    text='Approve'
                    controller={pending}
                    onClick={async (): Promise<void> => handleClose(true)}
                    buttonColor='primary'
                    circularColor='error'
                    circularSize='30px'
                    variant='contained'
                />
            </DialogActions>
        </Dialog>
    );
}

export default ApproveOrderDialog;
